import { supabase } from "../lib/supabase";
import { upsertUser, UserInfo } from "../lib/upsertUser";
import { validateScores } from "../utils/validation";

export const useSaveResult = () => {
  const save = async (
    userInfo: UserInfo,
    result: {
      primaryArchetype: string;
      secondaryArchetype: string;
      jaguar_score: number;
      condor_score: number;
      llama_score: number;
      quetzal_score: number;
      colibri_score: number;
      coqui_score: number;
      buho_score: number;
      score_data?: any;
    },
    categoryScores: any = {},
    selectedOptions: any[] = []
  ) => {
    try {
      // Validate scores
      const validation = validateScores({
        jaguar: result.jaguar_score,
        llama: result.llama_score,
        quetzal: result.quetzal_score,
        colibri: result.colibri_score,
        coqui: result.coqui_score,
        buho: result.buho_score,
        condor: result.condor_score || 0,
      });

      if (!validation.isValid) {
        throw new Error(`Invalid scores: ${validation.errors.join(", ")}`);
      }

      // Validate required fields
      if (!result.primaryArchetype || !result.secondaryArchetype) {
        throw new Error("Missing required archetype results");
      }

      // (1) Upsert user
      const user = await upsertUser(userInfo);
      if (!user?.id) {
        throw new Error("Failed to create or update user");
      }

      // (2) Store test result
      const { data: testResult, error: rErr } = await supabase
        .from("test_results")
        .insert({
          user_id: user.id,
          primary_archetype: result.primaryArchetype,
          secondary_archetype: result.secondaryArchetype,
          jaguar_score: Math.max(0, result.jaguar_score),
          condor_score: Math.max(
            0,
            result.mariposa_score || result.condor_score || 0
          ),
          llama_score: Math.max(0, result.llama_score),
          quetzal_score: Math.max(0, result.quetzal_score),
          colibri_score: Math.max(0, result.colibri_score),
          coqui_score: Math.max(0, result.coqui_score),
          buho_score: Math.max(0, result.buho_score),
          score_data: result.score_data || {},
          taken_at: new Date().toISOString(),
          categoryScores: categoryScores,
          selectedOptions: selectedOptions,
        })
        .select()
        .single();

      if (rErr) {
        console.log(rErr);
        console.error("Error saving test result:", rErr);
        throw new Error("Failed to save test results");
      }

      // (3) Email the results
      try {
        await supabase.functions.invoke("email-results-es", {
          body: {
            to: userInfo.email,
            archetype: result.primaryArchetype,
            shareUrl: window.location.href,
            reportId: testResult.id,
          },
        });
      } catch (emailError) {
        console.error("Error sending results email:", emailError);
        // Don't fail the operation for email errors
      }

      return testResult;
    } catch (error) {
      console.error("Error saving result:", error);
      throw error instanceof Error
        ? error
        : new Error("An unknown error occurred");
    }
  };

  return { save };
};
